import { useHubble } from '@seek/hubble';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import { Dialog, Stack, Text, Alert } from 'braid-design-system';
import { useEffect } from 'react';

import { useAppConfig } from 'src/modules/appConfig';
import { browserLogError } from 'src/modules/error-logger/browserLog';

import translations from '../AvatarContainer/.vocab';

interface Props {
  onClose: () => void;
  errorType: 'validation' | 'system';
  errorMessage?: string;
  errorData?: Record<string, unknown>;
}

export const ImageProcessingErrorDialog = ({
  onClose,
  errorType,
  errorMessage,
  errorData,
}: Props) => {
  const { t } = useTranslations(translations);
  const hubble = useHubble();
  const { zone, language } = useAppConfig();

  useEffect(() => {
    const text =
      errorType === 'validation' ? 'file too small' : 'file is corrupted';

    metrics.count('avatar_processing_error', [
      `error_type:${text}`,
      `error_message:${errorMessage}`,
    ]);

    hubble.trackEvent('component_viewed', {
      currentPageSection: 'photo preview',
      component: {
        type: 'card',
        name: errorMessage?.includes('Browser failed to encode JPEG.')
          ? 'avatar pre-upload error'
          : 'avatar upload error',
        text,
      },
      displayLanguage: language,
      zone,
    });

    browserLogError(new Error(errorMessage), {
      kind: 'MiddlewareError',
      name: 'ImageProcessingError',
      input: {
        ...(errorMessage && { message: errorMessage }),
        ...errorData,
      },
    });
  }, [errorType, errorMessage, errorData, hubble, language, zone]);

  return (
    <Dialog open onClose={onClose} title={t('Profile photo')} width="xsmall">
      <Alert tone="critical">
        <Stack space="medium">
          <Text weight="strong">
            {errorType === 'validation'
              ? t('Unable to upload photo')
              : t('Something went wrong')}
          </Text>
          <Text>
            {errorType === 'validation'
              ? t('Please make sure your photo is a standard image...')
              : t('Please try again or check back later.')}
          </Text>
        </Stack>
      </Alert>
    </Dialog>
  );
};
